import { FC } from 'react';
import {
  PiPause,
  PiPlay,
  PiStop,
  PiPhoneX,
  PiMicrophoneSlash,
  PiMicrophone,
  PiSkipForward,
  PiCaretDown,
} from 'react-icons/pi';
import { Text } from 'src/modules/shared/components/atoms/text';
import { DropdownItem } from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useSpeedDialControls } from '../../../hooks/speed-dialer/controls';
import Dialog from 'src/modules/shared/components/molecules/dialog';
import { formatPhoneNumber } from 'src/modules/shared/utils/formatting/phone.util';
import { AnimatedDot } from 'src/modules/shared/components/atoms/indicators/animated-dot';
import { classes } from 'src/modules/shared/utils/styles.util';

/**
 * Compact menu dropdown for speed dial controls.
 */
export const SpeedDialControlsMenu: FC = () => {
  const {
    isPaused,
    isCallActive,
    isMuted,
    currentQueueItem,
    currentPhoneNumber,
    currentAttempts,
    maxAttempts,
    isDialerActive,
    handleMute,
    handleEndCall,
    handlePlayPause,
    handleSkip,
    handleConfirmStop,
    showStopConfirmation,
    setShowStopConfirmation,
  } = useSpeedDialControls();

  const contactLabel = currentQueueItem ? currentQueueItem.contactLabel : '';
  const formattedPhoneNumber = currentPhoneNumber
    ? formatPhoneNumber(currentPhoneNumber)
    : '';
  const title = contactLabel || formattedPhoneNumber;

  const items: DropdownItem[] = [
    {
      onClick: handleMute,
      disabled: !isCallActive,
      content: (
        <div className="flex items-center gap-2 w-full">
          {isMuted ? (
            <PiMicrophoneSlash className="w-4 h-4 text-secondary-700" />
          ) : (
            <PiMicrophone className="w-4 h-4 text-secondary-700" />
          )}
          <Text size="paragraph" weight="light" color="secondary">
            {/* TODO: check when implementing i18n */}
            {isMuted ? 'Activar micrófono' : 'Silenciar micrófono'}
          </Text>
        </div>
      ),
    },
    {
      onClick: handleEndCall,
      disabled: !isCallActive,
      icon: <PiPhoneX className="w-4 h-4 text-secondary-700" />,
      // TODO: check when implementing i18n
      content: 'Terminar llamada',
    },
    {
      onClick: handleSkip,
      disabled: !isPaused,
      icon: <PiSkipForward className="w-4 h-4 text-secondary-700" />,
      // TODO: check when implementing i18n
      content: 'Saltar llamada',
    },
    {
      onClick: handlePlayPause,
      disabled: false,
      content: (
        <div className="flex items-center gap-2 w-full">
          {isPaused ? (
            <PiPlay className="w-4 h-4 text-secondary-700" />
          ) : (
            <PiPause className="w-4 h-4 text-secondary-700" />
          )}
          <Text size="paragraph" weight="light" color="secondary">
            {/* TODO: check when implementing i18n */}
            {isPaused ? 'Reanudar sesión' : 'Pausar sesión'}
          </Text>
        </div>
      ),
    },
    {
      onClick: () => setShowStopConfirmation(true),
      disabled: false,
      icon: <PiStop className="w-4 h-4 text-danger-700" />,
      // TODO: check when implementing i18n
      content: (
        <Tooltip
          content="Finaliza la sesión y la llamada actual"
          placement="bottom"
          size="sm"
        >
          <Text size="paragraph" weight="light" color="danger">
            {/* TODO: check when implementing i18n */}
            Finalizar sesión
          </Text>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="flex items-center gap-2">
      <MenuDropdown
        items={items}
        handlerComponent={
          <Button
            variant="primary"
            appearance="soft"
            size="md"
            className="rounded-full px-3 py-1 flex items-center gap-2 bg-call-50 hover:bg-call-100"
          >
            {isDialerActive && (
              <Tooltip
                // TODO: check when implementing i18n
                content={`Intento ${currentAttempts + 1} de ${maxAttempts}`}
                size="sm"
              >
                <div className="flex items-center gap-1">
                  {[...Array(maxAttempts)].map((_, attempt) => (
                    <AnimatedDot
                      key={attempt}
                      className={classes(
                        attempt < currentAttempts &&
                          'animate-none !bg-call-500',
                        attempt > currentAttempts &&
                          'animate-none !bg-neutral-200',
                        attempt === currentAttempts &&
                          (!isPaused ? '!bg-call-500' : '!bg-neutral-500')
                      )}
                      size="xs"
                      isPulsing={attempt === currentAttempts}
                    />
                  ))}
                </div>
              </Tooltip>
            )}
            <Text size="small" className="truncate max-w-[120px]">
              {title}
            </Text>
            <PiCaretDown className="w-4 h-4" />
          </Button>
        }
        placement="bottom"
        size="sm"
      />

      <Tooltip
        // TODO: check when implementing i18n
        content="Finaliza la sesión y la llamada actual"
        placement="bottom"
        size="sm"
      >
        <Button
          variant="danger"
          appearance="ghost"
          size="sm"
          onClick={() => setShowStopConfirmation(true)}
          className="!p-1.5"
          isIconOnly
        >
          <PiStop className="w-4 h-4" />
        </Button>
      </Tooltip>

      <Dialog
        isOpen={showStopConfirmation}
        onClose={() => setShowStopConfirmation(false)}
        // TODO: check when implementing i18n
        title="Confirmar"
        description={
          isCallActive
            ? '¿Estás seguro que deseas finalizar la sesión? La llamada actual será terminada.'
            : '¿Estás seguro que deseas finalizar la sesión?'
        }
        confirmLabel="Finalizar"
        cancelLabel="Cancelar"
        onConfirm={handleConfirmStop}
        variant="danger"
      />
    </div>
  );
};
